import { useCallback, useState } from "react";

import type {
  GroupVariableWithSync,
  GroupDimensionWithSync,
  GroupMeasureWithSync,
} from "@repo/shared";

// ---------------------------------------------------------------------------
// Form value shapes (what the in-panel detail forms edit)
// ---------------------------------------------------------------------------

export interface VariableForm {
  name: string;
  definition: string;
  description: string;
}

export interface DimensionFieldForm {
  def: string;
  label: string;
}

export interface DimensionForm {
  title: string;
  description: string;
  fields: DimensionFieldForm[];
  grouping: "N" | "H" | "C";
  labelExpression: string;
  tagsText: string;
}

export interface MeasureForm {
  title: string;
  description: string;
  expression: string;
  label: string;
  tagsText: string;
  numberFormat: string;
  color: string;
}

export type GroupItemKind = "variables" | "dimensions" | "measures";

// Temp ids for not-yet-created items are prefixed so the editor can tell a
// staged "create" apart from a persisted item id.
export const NEW_ID_PREFIX = "new:";

export function isNewId(id: string): boolean {
  return id.startsWith(NEW_ID_PREFIX);
}

export function newTempId(): string {
  return `${NEW_ID_PREFIX}${crypto.randomUUID()}`;
}

// ---------------------------------------------------------------------------
// Persisted item -> form, and empty forms
// ---------------------------------------------------------------------------

export function variableToForm(v: GroupVariableWithSync): VariableForm {
  return { name: v.name, definition: v.definition, description: v.description ?? "" };
}

export function emptyVariableForm(): VariableForm {
  return { name: "", definition: "", description: "" };
}

export function dimensionToForm(d: GroupDimensionWithSync): DimensionForm {
  const fields = d.fieldDefs.map((def, i) => ({ def, label: d.fieldLabels[i] ?? "" }));
  return {
    title: d.title,
    description: d.description ?? "",
    fields: fields.length > 0 ? fields : [{ def: "", label: "" }],
    grouping: d.grouping,
    labelExpression: d.labelExpression ?? "",
    tagsText: d.tags.join(", "),
  };
}

export function emptyDimensionForm(): DimensionForm {
  return {
    title: "",
    description: "",
    fields: [{ def: "", label: "" }],
    grouping: "N",
    labelExpression: "",
    tagsText: "",
  };
}

export function measureToForm(m: GroupMeasureWithSync): MeasureForm {
  return {
    title: m.title,
    description: m.description ?? "",
    expression: m.expression,
    label: m.label ?? "",
    tagsText: m.tags.join(", "),
    numberFormat: m.numberFormat ?? "",
    color: m.color ?? "",
  };
}

export function emptyMeasureForm(): MeasureForm {
  return {
    title: "",
    description: "",
    expression: "",
    label: "",
    tagsText: "",
    numberFormat: "",
    color: "",
  };
}

// ---------------------------------------------------------------------------
// One draft section (variables | dimensions | measures)
// ---------------------------------------------------------------------------

export interface DraftSection<TForm> {
  edits: Map<string, TForm>; // persisted item id -> staged form
  creates: Map<string, TForm>; // temp id -> staged form
  deletes: Set<string>; // persisted item id
  count: number;
  setEdit: (id: string, form: TForm) => void;
  removeEdit: (id: string) => void;
  setCreate: (tempId: string, form: TForm) => void;
  removeCreate: (tempId: string) => void;
  addDelete: (id: string) => void;
  removeDelete: (id: string) => void;
  clear: () => void;
}

function useDraftSection<TForm>(): DraftSection<TForm> {
  const [edits, setEdits] = useState<Map<string, TForm>>(new Map());
  const [creates, setCreates] = useState<Map<string, TForm>>(new Map());
  const [deletes, setDeletes] = useState<Set<string>>(new Set());

  const setEdit = useCallback((id: string, form: TForm) => {
    setEdits((prev) => new Map(prev).set(id, form));
  }, []);
  const removeEdit = useCallback((id: string) => {
    setEdits((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const setCreate = useCallback((tempId: string, form: TForm) => {
    setCreates((prev) => new Map(prev).set(tempId, form));
  }, []);
  const removeCreate = useCallback((tempId: string) => {
    setCreates((prev) => {
      const next = new Map(prev);
      next.delete(tempId);
      return next;
    });
  }, []);
  const addDelete = useCallback((id: string) => {
    setDeletes((prev) => new Set(prev).add(id));
  }, []);
  const removeDelete = useCallback((id: string) => {
    setDeletes((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const clear = useCallback(() => {
    setEdits(new Map());
    setCreates(new Map());
    setDeletes(new Set());
  }, []);

  return {
    edits,
    creates,
    deletes,
    count: edits.size + creates.size + deletes.size,
    setEdit,
    removeEdit,
    setCreate,
    removeCreate,
    addDelete,
    removeDelete,
    clear,
  };
}

// ---------------------------------------------------------------------------
// Combined draft for all three item kinds
// ---------------------------------------------------------------------------

export interface GroupItemsDraft {
  variables: DraftSection<VariableForm>;
  dimensions: DraftSection<DimensionForm>;
  measures: DraftSection<MeasureForm>;
  pendingCount: number;
  clearAll: () => void;
}

export function useGroupItemsDraft(): GroupItemsDraft {
  const variables = useDraftSection<VariableForm>();
  const dimensions = useDraftSection<DimensionForm>();
  const measures = useDraftSection<MeasureForm>();

  const clearAll = useCallback(() => {
    variables.clear();
    dimensions.clear();
    measures.clear();
  }, [variables, dimensions, measures]);

  return {
    variables,
    dimensions,
    measures,
    pendingCount: variables.count + dimensions.count + measures.count,
    clearAll,
  };
}
